import React, { useMemo, useState } from 'react';
import AssignPolicyIcon from '@svg/assign-policy.svg?react';
import ApplyDrafts from '@svg/apply-drafts.svg?react';
import './ecm-bulk-actions.scss';
import { ConnectionModel } from '../../connection.model';
import PolicyActionsModal from '../policy-actions-modal/policy-actions-modal';
import ApplyDraftsModal from './apply-drafts-modal';
import { observer } from 'mobx-react-lite';
import { useStores } from '../../../../store';
import {
    formatConnectionCount,
    isAllIneligibleSmart,
    isAllNonInitialConnections
} from '../../../../../utils/connection-utils';
import { ECMActionType, VersionLabel } from '../../../../../utils/enums';
import { EventType } from '../../../../../dtos/analytics.dto';

interface EcmBulkActionsProps {
    selectedConnections: Map<number, ConnectionModel>;
    setSelectedConnections: React.Dispatch<React.SetStateAction<Map<number, ConnectionModel>>>;
}

const EcmBulkActions: React.FC<EcmBulkActionsProps> = observer(({ selectedConnections, setSelectedConnections }) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isApplyDraftsModalVisible, setIsApplyDraftsModalVisible] = useState(false);
    const [isApplying, setIsApplying] = useState(false);

    const { customerStore } = useStores();

    const areAllSelectedDraft = useMemo(() =>
            selectedConnections.size > 0 && Array.from(selectedConnections.values()).every(
                (connection) => connection.policyStatus === VersionLabel.Draft
            )
        , [selectedConnections]);

    const handleNewPolicyClick = () => {
        customerStore.sendGeneralEvent(EventType.LinkClicked, { button: 'NewPolicy', number_of_sid: selectedConnections.size });
        setIsModalVisible(true);
    };

    const handleApplyDraftsClick = () => {
        customerStore.sendGeneralEvent(EventType.LinkClicked, { button: 'applyDrafts', number_of_sid: selectedConnections.size, location: 'ECM' });
        setIsApplyDraftsModalVisible(true);
    };

    const handleApplyDraftsCancel = () => {
        customerStore.sendGeneralEvent(EventType.LinkClicked, { button: 'cancel', location: 'applyDraftsModal' });
        setIsApplyDraftsModalVisible(false);
    };

    const handleApplyDraftsConfirm = async () => {
        customerStore.sendGeneralEvent(EventType.LinkClicked, { button: 'apply', location: 'applyDraftsModal' });
        const serviceIdList = Array.from(selectedConnections.keys());
        try {
            setIsApplying(true);
            await customerStore.applyBulkDrafts(serviceIdList);
            setSelectedConnections(new Map());
            customerStore.setToastData({
                title: 'Your request is being applied',
                desc: `${formatConnectionCount(serviceIdList.length, true)} now being applied. Once completed, you will be notified by email.`
            });
        } catch (error) {
            console.error('Error applying bulk drafts:', error);
            customerStore.setToastData({
                title: 'Error',
                desc: 'Failed to apply drafts. Please try again.'
            });
        } finally {
            setIsApplying(false);
            setIsApplyDraftsModalVisible(false);
        }
    };

    return (
        <div className="ecm-bulk-actions">
            <span className={`count ${selectedConnections.size === 0 ? 'empty':''}`}>{selectedConnections.size} selected</span>
            <div className="bulk-action-buttons">
                {selectedConnections.size > 0 &&
                    <button type="button" className="bulk-action-button" onClick={handleNewPolicyClick}><AssignPolicyIcon/>New Policy</button>}
                {areAllSelectedDraft &&
                    <button type="button" className="bulk-action-button" onClick={handleApplyDraftsClick}><ApplyDrafts/>Apply Drafts</button>}
            </div>
            <PolicyActionsModal
                initialAction={isAllNonInitialConnections(selectedConnections) && isAllIneligibleSmart(selectedConnections) ? ECMActionType.PullFromExisting : undefined}
                visible={isModalVisible}
                onClose={() => setIsModalVisible(false)}
                selectedConnections={selectedConnections}
                setSelectedConnections={setSelectedConnections}
            />
            <ApplyDraftsModal
                visible={isApplyDraftsModalVisible}
                onClose={handleApplyDraftsCancel}
                onConfirm={handleApplyDraftsConfirm}
                connectionCount={selectedConnections.size}
                isLoading={isApplying}
            />
        </div>
    );
});

export default EcmBulkActions;